import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine,
} from "recharts";
import { AlertCircle, Clock, Loader2, Plus, Ruler, Target } from "lucide-react";
import {
  ACCENT, RESPONSE_COLOR, fmtDate, Field, Checkbox, SummaryCard, HistoryTable,
} from "./shared";

export default function RecistTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    scan_type: "CT",
    lesion_diameters_mm: "20,10",
    new_lesions: false,
    all_target_disappeared: false,
    non_target_progression: false,
    is_baseline: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    const diameters = form.lesion_diameters_mm
      .split(",").map((x) => parseFloat(x.trim())).filter((x) => !isNaN(x));
    try {
      await api.post("/specialties/oncology/measurement", {
        date: form.date,
        scan_type: form.scan_type,
        lesion_diameters_mm: diameters,
        new_lesions: form.new_lesions,
        all_target_disappeared: form.all_target_disappeared,
        non_target_progression: form.non_target_progression,
        is_baseline: form.is_baseline,
        notes: form.notes || null,
      });
      toast.success("Tumor measurement recorded");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save measurement");
    } finally { setSaving(false); }
  }

  const chartData = useMemo(
    () => [...data.measurements].slice().reverse().map((m) => ({
      date: fmtDate(m.date),
      SLD: m.sld,
      response: m.response,
      change: m.change_from_baseline_pct,
    })),
    [data.measurements]
  );

  return (
    <div className="space-y-6" data-testid="onco-recist-tab">
      {data.summary?.latest_response && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="recist-summary">
          <SummaryCard title="Latest response"
                       value={data.summary.latest_response}
                       colored={RESPONSE_COLOR[data.summary.latest_response]}
                       icon={Target} />
          <SummaryCard title="Current SLD" value={`${data.summary.latest_sld} mm`} icon={Ruler} />
          <SummaryCard title="Baseline SLD" value={`${data.summary.baseline_sld} mm`} icon={Ruler} />
          <SummaryCard title="Measurements" value={data.summary.measurement_count} icon={Clock} />
        </div>
      )}

      <form onSubmit={submit} className="ros-card space-y-4" data-testid="recist-form">
        <div className="font-heading text-lg">Log tumor measurement (RECIST 1.1)</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="recist-input-date" className="ros-input" required />
          </Field>
          <Field label="Scan type">
            <select value={form.scan_type}
                    onChange={(e) => setForm({ ...form, scan_type: e.target.value })}
                    data-testid="recist-input-scan" className="ros-input">
              <option value="CT">CT</option>
              <option value="MRI">MRI</option>
              <option value="PET">PET</option>
              <option value="ultrasound">Ultrasound</option>
              <option value="physical_exam">Physical exam</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Lesion diameters (mm, comma-separated)">
            <input type="text" value={form.lesion_diameters_mm}
                   onChange={(e) => setForm({ ...form, lesion_diameters_mm: e.target.value })}
                   data-testid="recist-input-diameters"
                   placeholder="20, 10, 8" className="ros-input" required />
          </Field>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <Checkbox label="This IS the baseline scan"
                    checked={form.is_baseline}
                    onChange={(v) => setForm({ ...form, is_baseline: v })}
                    testId="recist-input-baseline" />
          <Checkbox label="New lesions appeared"
                    checked={form.new_lesions}
                    onChange={(v) => setForm({ ...form, new_lesions: v })}
                    testId="recist-input-new-lesions" />
          <Checkbox label="All target lesions disappeared"
                    checked={form.all_target_disappeared}
                    onChange={(v) => setForm({ ...form, all_target_disappeared: v })}
                    testId="recist-input-cr" />
          <Checkbox label="Non-target progression"
                    checked={form.non_target_progression}
                    onChange={(v) => setForm({ ...form, non_target_progression: v })}
                    testId="recist-input-nt-prog" />
        </div>

        <Field label="Notes">
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="recist-input-notes"
                    className="ros-input" />
        </Field>

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="recist-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="recist-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Save measurement"}
          </button>
        </div>
      </form>

      {chartData.length > 0 && (
        <div className="ros-card" data-testid="recist-chart">
          <div className="font-heading text-lg mb-3">Sum of longest diameters (SLD)</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <ReferenceLine y={data.summary?.baseline_sld}
                               stroke="#60A5FA" strokeDasharray="4 4"
                               label={{ value: "Baseline", fontSize: 10, fill: "#60A5FA" }} />
                <Line type="monotone" dataKey="SLD" stroke={ACCENT} strokeWidth={2}
                      // eslint-disable-next-line react/no-unstable-nested-components
                      dot={(props) => {
                        const r = data.measurements.slice().reverse()[props.index];
                        const color = RESPONSE_COLOR[r?.response] || ACCENT;
                        return <circle cx={props.cx} cy={props.cy} r={5} fill={color} stroke="none" />;
                      }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <HistoryTable
        title={`Imaging history (${data.measurements.length})`}
        testId="recist-history"
        rows={data.measurements}
        rowTestId={(r) => `recist-row-${r.id}`}
        empty="No measurements recorded."
        columns={[
          { label: "Date",  render: (r) => fmtDate(r.date) },
          { label: "Scan",  render: (r) => <span className="text-xs">{r.scan_type}</span> },
          { label: "SLD",   render: (r) => <span className="font-mono">{r.sld} mm</span> },
          { label: "Δ Baseline", render: (r) => (
            <span className="font-mono text-xs"
                  style={{ color: r.change_from_baseline_pct <= -30 ? "#4ADE80" :
                                  r.change_from_baseline_pct >= 20 ? "#F87171" : "#FBBF24" }}>
              {r.change_from_baseline_pct > 0 ? "+" : ""}{r.change_from_baseline_pct}%
            </span>
          )},
          { label: "Response", render: (r) => (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ color: RESPONSE_COLOR[r.response] || "#A3ACA7",
                           background: `${RESPONSE_COLOR[r.response] || "#A3ACA7"}18`,
                           border: `1px solid ${RESPONSE_COLOR[r.response] || "#A3ACA7"}40` }}>
              {r.response}
            </span>
          )},
          { label: "Lesions", render: (r) => (
            <span className="text-xs text-[#A3ACA7] font-mono">
              {(r.lesion_diameters_mm || []).join(" · ")}
            </span>
          )},
        ]}
      />
    </div>
  );
}
